import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Container, Paper, Card, CardContent, Button, IconButton, FormControl, Select, MenuItem
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import CloseIcon from '@mui/icons-material/Close';
import { apiGet } from '../api/apiClient';

export default function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [blogLoading, setBlogLoading] = useState(false);
  const [blogError, setBlogError] = useState('');
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest' or 'oldest'
  // Fetch blogs from API
  const fetchAllBlogs = async () => {
    try {
      setBlogError('');
      setBlogLoading(true);
      const data = await apiGet('/blogs');
      if (Array.isArray(data)) {
        const mapped = data.map(b => ({
          id: b.id,
          title: b.title,
          content: b.content,
          authorCode: b.author_code,
          is_published: b.is_published,
          createdAt: b.created_at || b.createdAt || new Date().toISOString(),
        }));
        const sorted = [...mapped].sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
          const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
          return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
        });
        setBlogs(sorted);
      } else {
        setBlogs([]);
        setBlogError('No AiroNotes returned from server');
      }
    } catch (e) {
      setBlogError(e.message || 'Unable to load AiroNotes');
      setBlogs([]);
    } finally {
      setBlogLoading(false);
    }
  };

  useEffect(() => {
    fetchAllBlogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOrder]);

  // Function to get preview text (strip HTML and truncate)
  const getPreview = (htmlContent, maxLength = 200) => {
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const text = tempDiv.textContent || tempDiv.innerText || '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const handleReadMore = (blog) => setSelectedBlog(blog);
  const handleCloseDialog = () => setSelectedBlog(null);

  return (
    <Container maxWidth="lg" sx={{ py: 4, mt: 8 }}>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 4, 
          bgcolor: '#0D004A',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', fontSize: '1.75rem' }}>
              AiroNotes
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', fontStyle: 'italic' }}>
              Exploring intelligence through thoughtful AI blogs
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 160, bgcolor: 'white', borderRadius: 1 }}>
            <Select
              value={sortOrder}
              label="Sort By"
              onChange={(e) => setSortOrder(e.target.value)}
              sx={{ color: '#333' }}
            >
              <MenuItem value="newest">Newest First</MenuItem>
              <MenuItem value="oldest">Oldest First</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 3 }}>
        <Box sx={{ width: selectedBlog ? { xs: '100%', md: '25%' } : '100%' }}>
          {blogLoading ? (
            <Paper sx={{ p: 6, textAlign: 'center' }}>
              <ArticleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                Loading AiroNotes…
              </Typography>
            </Paper>
          ) : blogError ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <ArticleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="error" sx={{ mb: 1 }}>
                {blogError}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                Please try again later.
              </Typography>
            </Paper>
          ) : blogs.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: 'center' }}>
              <ArticleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                No AiroNotes available
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                Check back later for new content
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {blogs.map((blog) => (
                <Card
                  key={blog.id}
                  sx={{
                    borderRadius: 2,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#0D004A',
                        mb: 1.5,
                        fontSize: '1.1rem',
                        textAlign: 'left',
                      }}
                    >
                      {blog.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#666',
                        lineHeight: 1.6,
                        mb: 2,
                        minHeight: '50px',
                        fontSize: '0.875rem',
                        textAlign: 'left',
                      }}
                    >
                      {getPreview(blog.content, 250)}
                    </Typography>
                    <Box sx={{ textAlign: 'left' }}>
                      <Button
                        variant="contained"
                        onClick={() => handleReadMore(blog)}
                        size="small"
                        sx={{
                          bgcolor: '#ff9800',
                          color: 'white',
                          textTransform: 'uppercase',
                          fontWeight: 600,
                          px: 2.5,
                          py: 0.75,
                          fontSize: '0.75rem',
                          '&:hover': {
                            bgcolor: '#f57c00',
                          },
                        }}
                      >
                        Read More
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>

        {selectedBlog && (
          <Box sx={{ width: { xs: '100%', md: '75%' } }}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ArticleIcon />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {selectedBlog?.title}
                  </Typography>
                </Box>
                <IconButton onClick={handleCloseDialog}>
                  <CloseIcon />
                </IconButton>
              </Box>

              <Box
                sx={{
                  '& p': {
                    marginBottom: '0.75rem',
                    lineHeight: 1.7,
                    color: '#333',
                    fontSize: '0.875rem',
                  },
                  '& h1': {
                    marginTop: '1rem',
                    marginBottom: '0.75rem',
                    color: '#0D004A',
                    fontWeight: 600,
                    fontSize: '1.5rem',
                  },
                  '& h2': {
                    marginTop: '1rem',
                    marginBottom: '0.75rem',
                    color: '#0D004A',
                    fontWeight: 600,
                    fontSize: '1.25rem',
                  },
                  '& h3': {
                    marginTop: '0.75rem',
                    marginBottom: '0.5rem',
                    color: '#0D004A',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                  },
                  '& h4, & h5, & h6': {
                    marginTop: '0.75rem',
                    marginBottom: '0.5rem',
                    color: '#0D004A',
                    fontWeight: 600,
                    fontSize: '1rem',
                  },
                  '& ul, & ol': {
                    marginLeft: '1.25rem',
                    marginBottom: '0.75rem',
                    fontSize: '0.875rem',
                  },
                  '& li': {
                    marginBottom: '0.25rem',
                    lineHeight: 1.6,
                  },
                  '& a': {
                    color: '#1976d2',
                    textDecoration: 'underline',
                    fontSize: '0.875rem',
                  },
                  '& img': {
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: 1,
                    margin: '0.75rem 0',
                  },
                }}
                dangerouslySetInnerHTML={{ __html: selectedBlog.content }}
              />
            </Paper>
          </Box>
        )}
      </Box>
    </Container>
  );
}

